import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ahoy from 'ahoy.js';
import { ArrowRight, Users } from 'lucide-react';
import FinancingInputCard from './FinancingInputCard';
import FinancingResultsPanel, { formatCompact } from './FinancingResultsPanel';
import ReadyToConnectWidget from '../shared/ReadyToConnectWidget';
import FeaturedPartnerCard from '../shared/FeaturedPartnerCard';
import {
  calculateFinancing,
  getInterestRate,
} from '../../Succession/Financing/financingCalculations';

function StatusDot({ variant = 'orange' }) {
  const colors = { orange: 'hsl(var(--succeedo-orange))', green: 'hsl(var(--chart-2))' };
  return (
    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: colors[variant] || colors.orange }} />
  );
}

const FinancingCalculatorPage = ({
  lenders,
  introductionRequests = [],
  onRequestIntroduction,
  introLoading = false,
  featuredPartners = [],
  onSaveScenario,
  defaultPurchasePrice = null,
  financingSnapshots = [],
  onSaveSnapshot,
  onLoadSnapshot,
  onSetPrimary,
  currentSnapshotId,
  readOnly = false,
  dealTeam = null,
  onShareWithTeam,
}) => {
  const [purchasePrice, setPurchasePrice] = useState(
    defaultPurchasePrice ? String(defaultPurchasePrice) : '500000'
  );
  const [downPaymentMode, setDownPaymentMode] = useState('percent');
  const [downPaymentDollar, setDownPaymentDollar] = useState(
    defaultPurchasePrice ? String(Math.round(defaultPurchasePrice * 0.25)) : '125000'
  );
  const [downPaymentPercent, setDownPaymentPercent] = useState(25);
  const [loanTerm, setLoanTerm] = useState(5);
  const [selectedLenderId, setSelectedLenderId] = useState(
    lenders.length > 0 ? lenders[0].id : null
  );
  const [customRate, setCustomRate] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved
  const scenarioSavedRef = useRef(false);

  // Track page view on mount
  useEffect(() => {
    ahoy.track('succeedo_financing_started');
  }, []);

  const price = parseFloat(purchasePrice) || 0;
  const downPayment =
    downPaymentMode === 'dollar'
      ? parseFloat(downPaymentDollar) || 0
      : price * (downPaymentPercent / 100);
  const loanAmount = Math.max(0, price - downPayment);

  const selectedLender = lenders.find((l) => String(l.id) === String(selectedLenderId));

  const lenderObj = useMemo(() => {
    if (customRate) {
      return { roi: parseFloat(customRate) / 100, rate_bands: [] };
    }
    if (selectedLender) {
      return selectedLender;
    }
    return { roi: 0.07, rate_bands: [] };
  }, [customRate, selectedLender]);

  const effectiveRate = useMemo(() => {
    return getInterestRate(lenderObj, loanAmount);
  }, [lenderObj, loanAmount]);

  const calculations = useMemo(() => {
    if (loanAmount <= 0) {
      return null;
    }
    return calculateFinancing(lenderObj, loanAmount, loanTerm * 12);
  }, [lenderObj, loanAmount, loanTerm]);

  // Fire onSaveScenario once when first valid calculation is computed
  useEffect(() => {
    if (calculations && !scenarioSavedRef.current && onSaveScenario) {
      scenarioSavedRef.current = true;
      const lenderName = customRate
        ? 'Custom Rate'
        : selectedLender?.name || 'Unknown';
      onSaveScenario({
        loan_amount: loanAmount,
        interest_rate: effectiveRate,
        loan_term_months: loanTerm * 12,
        monthly_payment: calculations.monthlyPayment || 0,
        total_interest: calculations.totalInterest || 0,
        total_cost: calculations.totalCost || 0,
        lender_name: lenderName,
      });
    }
  }, [calculations]);

  // Reset save status when inputs change
  useEffect(() => {
    if (saveStatus === 'saved') {
      setSaveStatus('idle');
    }
  }, [purchasePrice, downPaymentMode, downPaymentDollar, downPaymentPercent, loanTerm, selectedLenderId, customRate]);

  const handleSelectLender = (lenderId) => {
    setSelectedLenderId(lenderId);
    setCustomRate('');
  };

  const handleSaveSnapshot = async () => {
    if (!calculations || saveStatus === 'saving') return;

    setSaveStatus('saving');

    const lenderName = customRate ? 'Custom Rate' : selectedLender?.name || 'Unknown';

    const inputs = {
      purchase_price: parseFloat(purchasePrice) || 0,
      down_payment: downPayment,
      down_payment_mode: downPaymentMode,
      down_payment_percent: downPaymentPercent,
      loan_term_years: loanTerm,
      lender_id: selectedLenderId,
      lender_name: lenderName,
      interest_rate: effectiveRate,
    };

    const results = {
      loan_amount: loanAmount,
      monthly_payment: calculations.monthlyPayment,
      total_interest: calculations.totalInterest,
      total_cost: calculations.totalCost,
      effective_rate: effectiveRate,
      amortization_months: loanTerm * 12,
    };

    if (onSaveSnapshot) {
      const snapshot = await onSaveSnapshot({ inputs, results, lenderName });
      if (snapshot) {
        setSaveStatus('saved');
      } else {
        setSaveStatus('idle');
      }
    }
  };

  const loadSnapshot = (snap) => {
    const inputs = snap.inputs || {};
    setPurchasePrice(String(inputs.purchase_price || 500000));
    setDownPaymentMode(inputs.down_payment_mode || 'percent');
    setDownPaymentDollar(String(inputs.down_payment || 0));
    setDownPaymentPercent(inputs.down_payment_percent || 25);
    setLoanTerm(inputs.loan_term_years || 5);

    // Try to match lender by name
    if (inputs.lender_id) {
      const matchById = lenders.find((l) => String(l.id) === String(inputs.lender_id));
      if (matchById) {
        setSelectedLenderId(matchById.id);
        setCustomRate('');
      } else if (inputs.lender_name && inputs.lender_name !== 'Custom Rate') {
        const matchByName = lenders.find((l) => l.name === inputs.lender_name);
        if (matchByName) {
          setSelectedLenderId(matchByName.id);
          setCustomRate('');
        } else {
          setCustomRate(inputs.interest_rate ? String((inputs.interest_rate * 100).toFixed(1)) : '');
        }
      } else {
        setCustomRate(inputs.interest_rate ? String((inputs.interest_rate * 100).toFixed(1)) : '');
      }
    } else {
      setCustomRate(inputs.interest_rate ? String((inputs.interest_rate * 100).toFixed(1)) : '');
    }

    if (onLoadSnapshot) {
      onLoadSnapshot(snap);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Determine lender intro request state
  const pendingLenderIntro = introductionRequests.find(
    (r) => r.partner_category === 'lender' && (r.status === 'pending' || r.status === 'matched')
  );
  const lenderIntroState = pendingLenderIntro ? 'pending' : 'generic';

  // Only show lender partners on the financing calculator
  const displayPartners = featuredPartners.filter(
    (p) => p.partner_category === 'lender'
  );

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-12">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))]" />
          <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
            FINANCING CALCULATOR
          </span>
        </div>
        <h1 className="font-serif text-[42px] font-light text-foreground tracking-[-0.02em] mb-2">
          Model Your Deal Financing
        </h1>
        <p className="text-[16px] text-muted-foreground mb-6">
          See what a buyer's financing looks like — or model your own acquisition.
        </p>
      </div>

      {/* Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <FinancingInputCard
          purchasePrice={purchasePrice}
          setPurchasePrice={setPurchasePrice}
          downPaymentMode={downPaymentMode}
          setDownPaymentMode={setDownPaymentMode}
          downPaymentDollar={downPaymentDollar}
          setDownPaymentDollar={setDownPaymentDollar}
          downPaymentPercent={downPaymentPercent}
          setDownPaymentPercent={setDownPaymentPercent}
          loanTerm={loanTerm}
          setLoanTerm={setLoanTerm}
          selectedLenderId={selectedLenderId}
          onSelectLender={handleSelectLender}
          customRate={customRate}
          setCustomRate={setCustomRate}
          lenders={lenders}
          loanAmount={loanAmount}
          showValuationNote={!!defaultPurchasePrice}
        />
        <FinancingResultsPanel
          calculations={calculations}
          loanAmount={loanAmount}
          interestRate={effectiveRate}
          loanTerm={loanTerm}
        />
      </div>

      {/* Save Snapshot Button */}
      {calculations && !readOnly && (
        <div className="flex flex-col items-center gap-3 mt-8 mb-8">
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <button
              type="button"
              onClick={handleSaveSnapshot}
              disabled={saveStatus === 'saving'}
              className={`rounded-full px-6 py-3 font-medium transition-colors ${
                saveStatus === 'saved'
                  ? 'bg-[hsl(var(--chart-2))] text-white'
                  : 'bg-foreground text-background hover:bg-foreground/90'
              }`}
            >
              {saveStatus === 'saved'
                ? 'Saved \u2713'
                : saveStatus === 'saving'
                ? 'Saving\u2026'
                : 'Save This Scenario'}
            </button>
            {dealTeam && onShareWithTeam && (
              <button
                type="button"
                onClick={onShareWithTeam}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium border border-[hsl(var(--succeedo-orange))] text-[hsl(var(--succeedo-orange))] hover:bg-[hsl(var(--succeedo-orange))]/10 transition-colors"
                aria-label={`Share this scenario with ${dealTeam.name || 'your deal team'}`}
              >
                <Users className="w-3.5 h-3.5" />
                Share with team
              </button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground text-center">
            Save your current financing scenario to compare later
          </p>
        </div>
      )}

      {/* Saved Scenarios */}
      {financingSnapshots.length > 0 && (
        <div className="mb-8 pt-8 border-t border-border">
          <div className="flex items-center gap-2 mb-4">
            <StatusDot variant="orange" />
            <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
              SAVED SCENARIOS
            </span>
          </div>
          <div className="space-y-2">
            {financingSnapshots.slice(0, 5).map((snap) => {
              const date = snap.created_at
                ? new Date(snap.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : '';
              const lenderLabel = snap.lender_name || 'Unknown';
              const monthlyPayment = snap.results?.monthly_payment;
              const isPrimary = snap.is_primary;
              const isActive = String(snap.id) === String(currentSnapshotId);

              return (
                <div
                  key={snap.id}
                  className="flex items-center justify-between bg-muted border border-border rounded-[12px] px-4 py-3 hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-[13px] text-muted-foreground">{date}</span>
                    <span className="font-mono text-[10px] uppercase text-muted-foreground border border-border rounded-full px-2 py-0.5">
                      {lenderLabel}
                    </span>
                    <span className="text-[14px] font-semibold text-foreground">
                      {formatCompact(monthlyPayment)}/mo
                    </span>
                    {isPrimary && (
                      <span className="font-mono text-[10px] uppercase rounded-full px-2 py-0.5"
                            style={{ backgroundColor: 'hsl(var(--succeedo-orange) / 0.1)', color: 'hsl(var(--succeedo-orange))', border: '1px solid hsl(var(--succeedo-orange) / 0.2)' }}>
                        ★ Active
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {!isPrimary && onSetPrimary && (
                      <button
                        type="button"
                        onClick={() => onSetPrimary(snap.id)}
                        className="text-[12px] hover:underline transition-colors"
                        style={{ color: 'hsl(var(--succeedo-orange))' }}
                      >
                        Set as active &rarr;
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => loadSnapshot(snap)}
                      className="text-[13px] font-medium hover:underline"
                      style={{ color: 'hsl(var(--succeedo-orange))' }}
                    >
                      View
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Partner CTA Section */}
      <div className="mt-12 space-y-8">
        <div className="max-w-xl mx-auto">
          <ReadyToConnectWidget
            partnerCategory="lender"
            state={lenderIntroState}
            onRequestIntroduction={() => onRequestIntroduction && onRequestIntroduction('lender')}
            loading={introLoading}
            requestedTime={
              pendingLenderIntro?.created_at
                ? new Date(pendingLenderIntro.created_at).toLocaleDateString()
                : undefined
            }
          />
        </div>

        {displayPartners.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))]" />
              <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                FEATURED PARTNERS
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {displayPartners.map((partner) => (
                <FeaturedPartnerCard key={partner.id} partner={partner} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Next Step: Deal Structure */}
      <div className="bg-card border border-border border-l-2 border-l-[hsl(var(--succeedo-orange))] rounded-[16px] p-5 mt-8">
        <div className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide mb-2">
          NEXT STEP
        </div>
        <h3 className="text-[16px] font-semibold text-foreground mb-2">
          Model your deal structure
        </h3>
        <p className="text-[14px] text-muted-foreground mb-4">
          See how different deal structures affect your after-tax proceeds — cash, seller financing, earnout, or a blend.
        </p>
        <a href="/succeedo/deal-structure" className="inline-flex items-center gap-2 bg-foreground text-background rounded-full px-5 py-2.5 text-[14px] font-medium hover:bg-foreground/90 transition-colors">
          Explore Deal Structures <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

FinancingCalculatorPage.propTypes = {
  readOnly: PropTypes.bool,
  dealTeam: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
  }),
  onShareWithTeam: PropTypes.func,
};

FinancingCalculatorPage.defaultProps = {
  readOnly: false,
  dealTeam: null,
  onShareWithTeam: undefined,
};

export default FinancingCalculatorPage;
